'use client';

import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  AdvancedMarker,
  useAdvancedMarkerRef,
} from '@vis.gl/react-google-maps';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faPlusCircle,
  faUserDoctor,
} from '@fortawesome/pro-solid-svg-icons';
import { PulseButton } from 'src/components';
import { getSocTypeIcon } from 'src/components/TypeIcon/TypeIcon';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useGetMyChainedStrategiesQuery } from 'src/api/redux';
import { useSite } from 'src/context/providers/SiteProvider';
import { getMetricRows } from 'src/components/Maps/ProviderMarkers/getProviderPopupMetrics';
import { ProviderPopupDetails } from 'src/components/Maps/ProviderPopupDetails';
import StrategyMarkerCircle from '../StrategyMarkerCircle/StrategyMarkerCircle';
import { DEFAULT_MARKER_STYLE } from '../utils';

const STRATEGY_COLORS = [
  { dot: 'bg-orange-500', hover: 'bg-orange-700' },
  { dot: 'bg-teal-500', hover: 'bg-teal-700' },
  { dot: 'bg-purple-500', hover: 'bg-purple-700' },
  { dot: 'bg-sky-500', hover: 'bg-sky-700' },
  { dot: 'bg-pink-500', hover: 'bg-pink-700' },
  { dot: 'bg-green-500', hover: 'bg-green-700' },
  { dot: 'bg-red-500', hover: 'bg-red-700' },
];

export type DisplayOption = {
  code: string;
  type?: string;
  label?: string;
};

type StrategyFeatureMarkerProps = {
  listMember: any;
  position: { lat: number; lng: number };
  drawerId?: string;
  setSelectedProvider: (provider: any) => void;
  isHovered?: boolean;
  metricLabel?: string;
  metricData?: Record<string, number | any>;
  displayOptions?: DisplayOption[];
  onMarkerClick?: (memberId: string) => void;
  onMouseOver?: (memberId: string) => void;
  onMouseOut?: () => void;
  selectedProviders: any[] | null;
  setSelectedProviders: (providers: any[] | null) => void;
  onAddToList?: () => void;
};

const StrategyFeatureMarker = ({
  listMember,
  position,
  drawerId,
  setSelectedProvider,
  isHovered = false,
  metricLabel,
  metricData,
  displayOptions,
  onMarkerClick,
  onMouseOver,
  onMouseOut,
  selectedProviders,
  setSelectedProviders,
  onAddToList,
}: StrategyFeatureMarkerProps) => {
  const { closeAllButCurrent } = useDrawer();
  const { dispatch } = useSite();
  const [markerRef, marker] = useAdvancedMarkerRef();
  const { currentData: chainedStrategies } = useGetMyChainedStrategiesQuery();

  const chainColorMap = useMemo(() => {
    const allIds = (chainedStrategies?.results || [])
      .map((s: any) => s.id)
      .sort((a: number, b: number) => a - b);
    const map = new Map<number, { dot: string; hover: string }>();
    allIds.forEach((id: number, index: number) => {
      map.set(id, STRATEGY_COLORS[index % STRATEGY_COLORS.length]);
    });
    return map;
  }, [chainedStrategies?.results]);

  const chainIds: number[] = listMember?.chain_ids || [];

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const updateTooltipPosition = useCallback(() => {
    const markerElement = marker?.element as HTMLElement | null;
    const tooltipElement = tooltipRef.current;

    if (markerElement && tooltipElement && showTooltip) {
      const markerRect = markerElement.getBoundingClientRect();
      const tooltipHeight = tooltipElement.offsetHeight;
      const tooltipWidth = tooltipElement.offsetWidth;

      const top = markerRect.top + window.scrollY - tooltipHeight - 10;
      const left =
        markerRect.left +
        window.scrollX +
        markerRect.width / 2 -
        tooltipWidth / 2;

      setTooltipPosition({ top, left });
    }
  }, [marker, showTooltip]);

  useEffect(() => {
    if (showTooltip) {
      requestAnimationFrame(() => {
        updateTooltipPosition();
      });

      const handleUpdate = () => updateTooltipPosition();
      window.addEventListener('scroll', handleUpdate, {
        capture: true,
        passive: true,
      });
      window.addEventListener('resize', handleUpdate, { passive: true });

      return () => {
        window.removeEventListener('scroll', handleUpdate, {
          capture: true,
        } as any);
        window.removeEventListener('resize', handleUpdate, {
          passive: true,
        } as any);
      };
    }
  }, [showTooltip, updateTooltipPosition]);

  const handleMarkerClick = useCallback(() => {
    if (onMarkerClick && listMember?.provider_id) {
      onMarkerClick(listMember.provider_id.toString());
    }
  }, [onMarkerClick, listMember]);

  const handleMouseOver = useCallback(() => {
    setShowTooltip(true);
    if (onMouseOver && listMember?.provider_id) {
      onMouseOver(listMember.provider_id.toString());
    }
  }, [onMouseOver, listMember]);

  const handleMouseOut = useCallback(() => {
    // Delay to allow mouse to move to tooltip
    setTimeout(() => {
      if (!tooltipRef.current?.matches(':hover')) {
        setShowTooltip(false);
        if (onMouseOut) {
          onMouseOut();
        }
      }
    }, 100);
  }, [onMouseOut]);

  const handleTooltipMouseLeave = useCallback(() => {
    setShowTooltip(false);
    if (onMouseOut) {
      onMouseOut();
    }
  }, [onMouseOut]);

  const handleAddToList = useCallback(() => {
    if (!listMember) return;

    // Add the provider to selectedProviders if not already selected
    const currentProviders = selectedProviders || [];
    const isAlreadySelected = currentProviders.some(
      (p) => p.provider_id === listMember.provider_id
    );

    if (!isAlreadySelected) {
      setSelectedProviders([...currentProviders, listMember]);
    }

    // Open the add to list dialog
    if (onAddToList) {
      onAddToList();
    }
  }, [listMember, selectedProviders, setSelectedProviders, onAddToList]);

  const handleProviderClick = useCallback(
    (provider) => {
      setSelectedProvider(provider);
      dispatch({
        type: 'SET_CURRENT_PROVIDER',
        payload: { id: provider.provider_id, type: provider.type || null },
      });
      closeAllButCurrent('profile');
    },
    [closeAllButCurrent, dispatch, setSelectedProvider]
  );

  const popupMetrics = useMemo(
    () => getMetricRows(displayOptions, metricData),
    [displayOptions, metricData]
  );

  const tooltipContent = showTooltip && isMounted && (
    <div
      ref={tooltipRef}
      onMouseLeave={handleTooltipMouseLeave}
      className="fixed w-56 z-[2000] overflow-visible pointer-events-auto"
      style={{
        top: `${tooltipPosition?.top ?? 0}px`,
        left: `${tooltipPosition?.left ?? 0}px`,
        opacity: tooltipPosition ? 1 : 0,
        transition: 'opacity 0.1s ease-in-out',
      }}
    >
      {/* Invisible connector area to maintain hover when moving from marker to tooltip */}
      <div
        className="absolute h-6 w-8 left-1/2 bottom-0 -mb-6 transform -translate-x-1/2 bg-transparent"
        aria-hidden="true"
      />

      <div className="rounded-lg bg-white shadow-xl border border-neutral-200 flex flex-col overflow-hidden relative">
        <div className="overflow-y-auto flex-1 max-h-72">
          <div className="flex flex-col gap-1 border-b border-neutral-100 px-3 py-2 hover:bg-neutral-50">
            <ProviderPopupDetails
              name={listMember?.name}
              specialty={listMember?.specialty}
              addressLine1={listMember?.address_line_1}
              city={listMember?.city}
              state={listMember?.state}
              onNameClick={() => handleProviderClick(listMember)}
              metricLabel={metricLabel}
              metrics={popupMetrics}
            />
          </div>
        </div>

        <div className="flex sticky bottom-0 z-10 items-center gap-2 border-t border-neutral-200 bg-neutral-50 px-3 py-2">
          <PulseButton
            variant="solid"
            color="primary"
            size="sm"
            onClick={handleAddToList}
            startDecorator={faPlusCircle}
            className="w-full"
          >
            Add to List
          </PulseButton>
        </div>
      </div>

      {/* Arrow pointing down */}
      <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-1.5">
        <div className="w-3 h-3 bg-white border-l border-b border-neutral-200 transform rotate-45"></div>
      </div>
    </div>
  );

  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={position}
        onClick={handleMarkerClick}
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseOut}
        zIndex={isHovered ? 1000 : 1}
      >
        {(() => {
          const isSingleStrategy = chainIds.length === 1;
          const singleStrategyColors = isSingleStrategy
            ? chainColorMap.get(chainIds[0]) || STRATEGY_COLORS[0]
            : undefined;

          return (
            <div className="relative">
              <StrategyMarkerCircle
                isHovered={isHovered}
                className="cursor-pointer"
                colorClass={singleStrategyColors?.dot || DEFAULT_MARKER_STYLE.colorClass}
                hoverColorClass={
                  singleStrategyColors?.hover ||
                  DEFAULT_MARKER_STYLE.hoverColorClass
                }
              >
                <Icon
                  icon={
                    listMember.type === 'hcp'
                      ? faUserDoctor
                      : getSocTypeIcon(listMember.soc_type)
                  }
                  className="text-white text-sm"
                />
              </StrategyMarkerCircle>
              {chainIds.length > 1 && (
                <div
                  className="absolute top-0.5 right-0.5 flex -space-x-0.5 z-10"
                  data-testid="strategy-marker-dots"
                  aria-label={`${chainIds.length} strategies`}
                >
                  {chainIds.map((id) => (
                    <span
                      key={id}
                      data-testid={`strategy-dot-${id}`}
                      className={`w-2 h-2 rounded-full ring-1 ring-white ${chainColorMap.get(id)?.dot || STRATEGY_COLORS[0].dot}`}
                      aria-hidden="true"
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </AdvancedMarker>
      {isMounted && createPortal(tooltipContent, document.body)}
    </>
  );
};

export default StrategyFeatureMarker;
